/**
 * Shared "listing attribute" definitions - New, Open House, Favorites,
 * Alerts, Price Change. An attribute means three things everywhere it's
 * used: a visibility rule ("Status OR Attribute" - a listing shows even if
 * its own status is toggled off, as long as it matches an active
 * attribute), a within-section sort, and a badge. This file holds the
 * pure, DOM-free pieces (types + matching logic) shared by both the
 * frontend apps and any backend worker that needs to reason about the same
 * concepts. The UI-facing registry (icons, colors, labels, sort
 * comparators) lives in @slowmaui/ui-components, which is allowed to
 * depend on this package (not the other way around).
 *
 * Two different "kinds" of attribute exist:
 * - data-predicate: computed from the listing's own fields (New, Open
 *   House, Price Change) - no external state needed.
 * - id-set: per-user curated membership (Favorites, Alerts, Agent Picks,
 *   Featured) - matched against a Set<string> of listingItemKey() values
 *   supplied by the caller. Agent Picks and Featured both differ from
 *   Favorites/Alerts in *how* their sets get populated (opening a
 *   `?story=`/`?agent=` link, not per-item user clicks - see apps/public's
 *   utils/agentPicks.ts and utils/agentFeatured.ts) but are otherwise
 *   identical plumbing, including reusing the same `nh:<id>` prefix
 *   convention for neighborhoods. Agent Picks and Featured differ from
 *   *each other* in lifetime and exclusivity, not in how they're matched
 *   here - see agentFeatured.ts's own doc comment.
 *
 * matchesAttribute() is shape-agnostic: it takes a normalized
 * AttributeFacts object rather than a raw ListingSummary or
 * TmkStatusMarker, so the same matching logic works against both of this
 * app's two listing shapes (the "slow path" full listings and the "fast
 * path" map markers) via two small adapters instead of two separate
 * hand-written matcher functions.
 */
import { isNewListing, listingItemKey } from './listing.js';
export const ATTRIBUTE_KIND = {
    favorites: 'id-set',
    alerts: 'id-set',
    agentPicks: 'id-set',
    featured: 'id-set',
    openHouse: 'data-predicate',
    newListing: 'data-predicate',
    priceChange: 'data-predicate',
};
export function factsFromListingSummary(listing) {
    return {
        itemKey: listingItemKey(listing),
        listDate: listing.listDate,
        hasOpenHouse: !!listing.openHouse,
        hasPriceChange: !!listing.hasPriceChange,
    };
}
export function factsFromTmkStatusMarker(marker) {
    return {
        itemKey: marker.tmk,
        listDate: marker.listingDate,
        hasOpenHouse: marker.hasOpenHouse,
        hasPriceChange: marker.hasPriceChange,
    };
}
/** Whether a listing (already reduced to facts) matches one attribute. */
export function attributeMatches(id, facts, idSets) {
    switch (id) {
        case 'favorites':
            return !!idSets.favorites?.has(facts.itemKey);
        case 'alerts':
            return !!idSets.alerts?.has(facts.itemKey);
        case 'agentPicks':
            return !!idSets.agentPicks?.has(facts.itemKey);
        case 'featured':
            return !!idSets.featured?.has(facts.itemKey);
        case 'openHouse':
            return facts.hasOpenHouse;
        case 'newListing':
            return isNewListing(facts.listDate ? { listDate: facts.listDate } : {});
        case 'priceChange':
            return facts.hasPriceChange;
    }
}
//# sourceMappingURL=attributes.js.map